import api from "@/lib/api/client";

export const employeeApi = {
  list: (params) => api.get("/employees", { params }).then((r) => ({ items: r.data, meta: r.meta })),
  // Independent of the status filter itself -- every summary card's own
  // count needs to reflect the whole scoped population regardless of
  // which status the list itself currently has selected. See
  // employee.service.js's own getStatusSummary for why. Every other
  // filter (department, search, ...) is still passed through and still
  // applies -- just never status.
  getStatusSummary: (params) => api.get("/employees/status-summary", { params: { ...params, status: undefined } }).then((r) => r.data),
  // Pre-shaped for SearchableSelect's loadOptions -- every picker (reports
  // to, hiring manager, interview panel) needs the exact same {value, label,
  // sublabel} mapping from an Employee document, so it lives here once
  // instead of being copied into every component that needs a person picker.
  searchOptions: (params) =>
    api.get("/employees", { params: { ...params, limit: params?.limit ?? 20 } }).then((r) =>
      r.data
        .filter((e) => !params?.excludeId || e._id !== params.excludeId)
        .map((e) => ({
          value: e._id,
          label: `${e.personal.firstName} ${e.personal.lastName}`,
          sublabel: `${e.employeeCode}${e.employment?.designationId?.title ? ` · ${e.employment.designationId.title}` : ""}`,
        }))
    ),
  // Same shape as searchOptions, but backed by /employees/directory, which
  // every employee may read -- for pickers a plain employee uses (e.g. leave
  // handover), where /employees would be denied without employee:read.
  directoryOptions: (params) =>
    api.get("/employees/directory", { params: { q: params?.q, limit: params?.limit ?? 20 } }).then((r) =>
      r.data
        .filter((e) => !params?.excludeId || e._id !== params.excludeId)
        .map((e) => ({
          value: e._id,
          label: `${e.personal.firstName} ${e.personal.lastName}`,
          sublabel: `${e.employeeCode}${e.employment?.designationId?.title ? ` · ${e.employment.designationId.title}` : ""}`,
        }))
    ),
  get: (id, params) => api.get(`/employees/${id}`, { params }).then((r) => r.data),
  create: (payload) => api.post("/employees", payload).then((r) => r.data),
  update: (id, payload) => api.patch(`/employees/${id}`, payload).then((r) => r.data),
  history: (id) => api.get(`/employees/${id}/history`).then((r) => r.data),
  addHistory: (id, entry) => api.post(`/employees/${id}/history`, entry).then((r) => r.data),
  // IT/HR provisioning -- sets up a laptop before the employee's first
  // ever check-in (see agent.service.js's own pairDevice for how this
  // differs from the code check-in itself issues). Returns the raw code
  // once; there is no way to retrieve it again afterward, only regenerate.
  generateAgentProvisioningCode: (id) => api.post(`/employees/${id}/agent-provisioning-code`).then((r) => r.data),
  // HR/Admin directly setting a new password for this employee's own
  // login -- see employee.controller.js's own setPassword for why this
  // is distinct from the self-service forgot-password flow.
  setPassword: (id, password) => api.post(`/employees/${id}/set-password`, { password }).then((r) => r.data),
  // Self-service -- see CheckInWidget.jsx's own MonitoringRequiredPanel,
  // the "get my pairing code" button an employee uses to set up their
  // own device before their first ever check-in.
  generateMyAgentProvisioningCode: () => api.post("/employees/me/agent-provisioning-code").then((r) => r.data),
  team: (id) => api.get(`/employees/${id}/team`).then((r) => r.data),
  deactivate: (id) => api.post(`/employees/${id}/deactivate`).then((r) => r.data),
  reactivate: (id) => api.post(`/employees/${id}/reactivate`).then((r) => r.data),
  importFromCsv: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/employees/import", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  // Blob, not JSON -- this is a file download, so the unwrap interceptor's
  // { success, data, meta } envelope check never applies here.
  exportDetails: (params) => api.get("/employees/export-details", { params }).then((r) => r.data),
  exportCsv: (params) => api.get("/employees/export", { params, responseType: "blob" }).then((r) => r.data),
  // Who has filled in the public birthday link, and when -- see
  // EmployeeListPage.jsx's BirthdayLinkDialog.
  exportCelebrationDobSubmissions: () =>
    api.get("/employees/celebration-dob-submissions/export", { responseType: "blob" }).then((r) => r.data),
  // Same signed-URL pattern as the company logo -- getPhotoUrl returns a
  // short-lived URL, never the raw file directly.
  // The signed-in employee's own photo: { url } (null when none).
  getMyPhotoUrl: () => api.get("/employees/me/photo-url").then((r) => r.data),
  uploadMyPhoto: (file) => {
    const formData = new FormData();
    formData.append("photo", file);
    return api.post("/employees/me/photo", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  removeMyPhoto: () => api.delete("/employees/me/photo").then((r) => r.data),
  getPhotoUrl: (id) => api.get(`/employees/${id}/photo-url`).then((r) => r.data),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append("photo", file);
    return api.post(`/employees/${id}/photo`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  me: () => api.get("/employees/me").then((r) => r.data),
  directory: (params) => api.get("/employees/directory", { params }).then((r) => ({ items: r.data, meta: r.meta })),
  orgChart: () => api.get("/employees/org-chart").then((r) => r.data),
};

export const orgApi = {
  departments: () => api.get("/org/departments").then((r) => r.data),
  designations: () => api.get("/org/designations").then((r) => r.data),
  locations: () => api.get("/org/locations").then((r) => r.data),
  holidays: (year) => api.get("/org/holidays", { params: { year } }).then((r) => r.data),
  shifts: () => api.get("/org/shifts").then((r) => r.data),
  profile: () => api.get("/org/profile").then((r) => r.data),
  updateProfile: (payload) => api.patch("/org/profile", payload).then((r) => r.data),

  // The write-capable addition to the plain-API-key read-only Drive sync
  // (see googleDriveOAuth.service.js on the backend). connect() returns a
  // real Google consent-screen URL -- the caller navigates the browser
  // there directly (window.location.href), not another API call; this
  // has to be a genuine top-level page redirect for Google's own OAuth
  // flow to work at all.
  driveOAuthConnect: () => api.post("/org/drive-oauth/connect").then((r) => r.data),
  driveOAuthStatus: () => api.get("/org/drive-oauth/status").then((r) => r.data),
  driveOAuthDisconnect: () => api.post("/org/drive-oauth/disconnect").then((r) => r.data),

  // Company logo -- embedded directly in generated documents (offer
  // letters today). getLogoUrl returns a short-lived signed URL, same
  // pattern as resume/offer PDF downloads elsewhere in this app.
  getLogoUrl: () => api.get("/org/logo-url").then((r) => r.data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append("logo", file);
    return api.post("/org/logo", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  getSealUrl: () => api.get("/org/seal-url").then((r) => r.data),
  uploadSeal: (file) => {
    const formData = new FormData();
    formData.append("seal", file);
    return api.post("/org/seal", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  getHrSignatureUrl: () => api.get("/org/hr-signature-url").then((r) => r.data),
  uploadHrSignature: (file) => {
    const formData = new FormData();
    formData.append("signature", file);
    return api.post("/org/hr-signature", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  getHeadSignatureUrl: () => api.get("/org/head-signature-url").then((r) => r.data),
  uploadHeadSignature: (file) => {
    const formData = new FormData();
    formData.append("signature", file);
    return api.post("/org/head-signature", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },

  // Desktop tracker agent installers / browser extension package -- the
  // uploaded-file alternative to a plain download URL (see updateProfile's
  // desktopAgentDownloadUrlWindows/Mac/browserExtensionDownloadUrl fields
  // above for the URL half of this). getDesktopAgentDownloadUrl is the one
  // CheckInWidget.jsx's own download buttons should call -- it resolves to
  // whichever of an uploaded file or a plain URL the org actually has set
  // right now (an uploaded file always wins when both are set), rather
  // than the buttons reading the raw profile fields directly.
  uploadDesktopAgentWindows: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/org/desktop-agent-windows", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  removeDesktopAgentWindows: () => api.delete("/org/desktop-agent-windows").then((r) => r.data),
  uploadDesktopAgentMac: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/org/desktop-agent-mac", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  removeDesktopAgentMac: () => api.delete("/org/desktop-agent-mac").then((r) => r.data),
  uploadBrowserExtension: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/org/browser-extension", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  removeBrowserExtension: () => api.delete("/org/browser-extension").then((r) => r.data),
  getDesktopAgentDownloadUrl: (platform) => api.get(`/org/desktop-agent-download-url/${platform}`).then((r) => r.data),

  // SMTP server settings -- secrets always come back masked ("••••1234"),
  // never in full, once saved. See smtpSetting.service.js.
  getSmtpSettings: () => api.get("/org/smtp-settings").then((r) => r.data),
  saveSmtpSettings: (payload) => api.put("/org/smtp-settings", payload).then((r) => r.data),
  clearSmtpSettings: () => api.delete("/org/smtp-settings").then((r) => r.data),
  testSmtpSettings: (to) => api.post("/org/smtp-settings/test", to ? { to } : {}).then((r) => r.data),
  // Every email this app has ever sent -- interview invites, resets, all of
  // it -- with a real sent/failed outcome per row, not a guess.
  listEmailLogs: (params) => api.get("/org/email-logs", { params }).then((r) => ({ items: r.data, meta: r.meta })),

  createShift: (payload) => api.post("/org/shifts", payload).then((r) => r.data),
  updateShift: (id, payload) => api.patch(`/org/shifts/${id}`, payload).then((r) => r.data),
  deleteShift: (id) => api.delete(`/org/shifts/${id}`).then((r) => r.data),
  setDefaultShift: (shiftId) => api.patch("/org/shifts/default", { shiftId }).then((r) => r.data),

  createDepartment: (payload) => api.post("/org/departments", payload).then((r) => r.data),
  updateDepartment: (id, payload) => api.patch(`/org/departments/${id}`, payload).then((r) => r.data),
  deleteDepartment: (id) => api.delete(`/org/departments/${id}`).then((r) => r.data),

  createDesignation: (payload) => api.post("/org/designations", payload).then((r) => r.data),
  updateDesignation: (id, payload) => api.patch(`/org/designations/${id}`, payload).then((r) => r.data),
  deleteDesignation: (id) => api.delete(`/org/designations/${id}`).then((r) => r.data),

  createLocation: (payload) => api.post("/org/locations", payload).then((r) => r.data),
  updateLocation: (id, payload) => api.patch(`/org/locations/${id}`, payload).then((r) => r.data),
  deleteLocation: (id) => api.delete(`/org/locations/${id}`).then((r) => r.data),

  createHoliday: (payload) => api.post("/org/holidays", payload).then((r) => r.data),
  updateHoliday: (id, payload) => api.patch(`/org/holidays/${id}`, payload).then((r) => r.data),
  deleteHoliday: (id) => api.delete(`/org/holidays/${id}`).then((r) => r.data),
  suggestHolidayDates: (year, holidayNames) => api.post("/org/holidays/suggest-dates", { year, holidayNames }).then((r) => r.data),
  suggestRoleDescriptions: (roleNames) => api.post("/org/designations/suggest-descriptions", { roleNames }).then((r) => r.data),

  // The shareable "employees, add the asset you're using" link -- generated
  // lazily on the backend the first time this is called. regenerate rotates
  // it, invalidating whatever link was shared before (see AssetsPage.jsx's
  // SelfRegisterLinkDialog).
  getAssetSelfRegisterLink: () => api.get("/org/asset-self-register-link").then((r) => r.data),
  regenerateAssetSelfRegisterLink: () => api.post("/org/asset-self-register-link/regenerate").then((r) => r.data),

  // The shareable "tell us your real birthday" link -- unlike
  // getAssetSelfRegisterLink above, this is one fixed link derived from the
  // organization's own name, not a rotating secret (see
  // EmployeeListPage.jsx's BirthdayLinkDialog).
  getCelebrationDobLink: () => api.get("/org/celebration-dob-link").then((r) => r.data),
};
